import EntityManager from "@/components/EntityManager";
import { inr, fmtDate, today } from "@/lib/format";
import { TableRow, TableCell } from "@/components/ui/table";
import { printBankStatement, printBankPassbook } from "@/lib/print";
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Printer, Plus, Trash2, Minus, Rows3, BarChart3 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";

const EXP_KEYS = ["loading_charge", "unloading_charge", "vehicle_hire", "vehicle_advance", "vehicle_balance", "salary_expenses", "office_rent", "other_expenses"];
const BRANCH_COLORS = { Chennai: "#1d4ed8", Tada: "#10b981", Hyderabad: "#f59e0b", Jaipur: "#8b5cf6" };

function MonthlySummaryDialog({ open, onOpenChange, rows }) {
  const branches = ["Chennai", "Tada", "Hyderabad", "Jaipur"];
  const byMonth = {};
  (rows || []).forEach((r) => {
    const m = String(r.transaction_date || "").slice(0, 7);
    if (!m) return;
    const b = r.branch || "Chennai";
    byMonth[m] = byMonth[m] || {};
    byMonth[m][b] = (byMonth[m][b] || 0) + EXP_KEYS.reduce((s, k) => s + (Number(r[k]) || 0), 0);
  });
  const months = Object.keys(byMonth).sort();
  const label = (m) => new Date(`${m}-01`).toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
  const data = months.map((m) => ({ month: label(m), ...branches.reduce((o, b) => ({ ...o, [b]: byMonth[m][b] || 0 }), {}) }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader><DialogTitle className="font-display">Bank Expense — Monthly Summary by Branch</DialogTitle></DialogHeader>
        {months.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No bank entries yet.</p>
        ) : (
          <div className="space-y-4">
            <div className="h-[260px]" data-testid="bank-summary-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" fontSize={11} />
                  <YAxis fontSize={11} tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : v)} />
                  <Tooltip formatter={(v) => inr(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {branches.map((b) => <Bar key={b} dataKey={b} fill={BRANCH_COLORS[b]} radius={[3, 3, 0, 0]} />)}
                </BarChart>
              </ResponsiveContainer>
            </div>
            <table className="w-full text-sm" data-testid="bank-summary-table">
              <thead>
                <tr className="border-b text-left text-xs uppercase text-slate-500">
                  <th className="py-1.5">Month</th>
                  {branches.map((b) => <th key={b} className="py-1.5 text-right">{b}</th>)}
                  <th className="py-1.5 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {months.map((m) => {
                  const tot = branches.reduce((s, b) => s + (byMonth[m][b] || 0), 0);
                  return (
                    <tr key={m} className="border-b border-slate-100">
                      <td className="py-1.5 font-medium">{label(m)}</td>
                      {branches.map((b) => <td key={b} className="py-1.5 text-right">{byMonth[m][b] ? inr(byMonth[m][b]) : "—"}</td>)}
                      <td className="py-1.5 text-right font-semibold">{inr(tot)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const BLANK_ROW = () => ({ branch: "Chennai", transaction_date: today(), cheque_no: "", mode: "", mr_no: "", mr_date: "", mr_amount: "", deposit_date: "", withdrawal_date: "", credit_amount: "", loading_charge: "", unloading_charge: "", vehicle_hire: "", vehicle_advance: "", vehicle_balance: "", salary_expenses: "", office_rent: "", other_expenses: "", balance: "", narration: "", remarks: "" });
const NUM_KEYS = ["credit_amount", "mr_amount", "loading_charge", "unloading_charge", "vehicle_hire", "vehicle_advance", "vehicle_balance", "salary_expenses", "office_rent", "other_expenses", "balance"];

function BankHeaderTools({ rows }) {
  const [branch, setBranch] = useState("All");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [entries, setEntries] = useState([BLANK_ROW()]);
  const [saving, setSaving] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const upd = (i, k, v) => setEntries((es) => es.map((e, j) => (j === i ? { ...e, [k]: v } : e)));
  const saveAll = async () => {
    setSaving(true);
    try {
      let n = 0;
      for (const e of entries) {
        if (!e.transaction_date && !e.narration && !e.balance) continue;
        const body = { ...e };
        NUM_KEYS.forEach((k) => (body[k] = Number(body[k]) || 0));
        await api.post("/bank-statements", body);
        n++;
      }
      toast.success(`${n} entries saved`);
      setOpen(false);
      setMinimized(false);
      setEntries([BLANK_ROW()]);
      window.location.reload();
    } catch {
      toast.error("Could not save entries");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={branch} onValueChange={setBranch}>
        <SelectTrigger className="h-9 w-[110px] text-sm" data-testid="passbook-branch"><SelectValue /></SelectTrigger>
        <SelectContent>{["All", "Chennai", "Tada", "Hyderabad", "Jaipur"].map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
      </Select>
      <Input type="date" className="h-9 w-[140px] text-sm" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="passbook-from" />
      <Input type="date" className="h-9 w-[140px] text-sm" value={to} onChange={(e) => setTo(e.target.value)} data-testid="passbook-to" />
      <Button variant="outline" onClick={() => printBankPassbook(branch, from, to, rows)} data-testid="print-passbook-btn">
        <Printer className="mr-2 h-4 w-4" /> Passbook
      </Button>
      <Button variant="outline" onClick={() => setSummaryOpen(true)} data-testid="bank-summary-btn">
        <BarChart3 className="mr-2 h-4 w-4" /> Monthly Summary
      </Button>
      <MonthlySummaryDialog open={summaryOpen} onOpenChange={setSummaryOpen} rows={rows} />
      <Button onClick={() => { setOpen(true); setMinimized(false); }} data-testid="multi-entry-btn">
        <Rows3 className="mr-2 h-4 w-4" /> Multi Entry
      </Button>
      {minimized && (
        <button
          type="button"
          onClick={() => { setMinimized(false); setOpen(true); }}
          className="fixed bottom-4 right-4 z-50 rounded-full bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-lg hover:bg-slate-700"
          data-testid="multi-entry-maximize-btn"
        >
          ▲ Bank Multi Entry ({entries.length} rows)
        </button>
      )}
      <Dialog open={open} onOpenChange={(v) => { if (!v) setMinimized(true); setOpen(v); }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[95vw]">
          <DialogHeader>
            <div className="flex items-center justify-between pr-8">
              <DialogTitle className="font-display">Bank Statement — Multiple Entry</DialogTitle>
              <Button variant="outline" size="sm" onClick={() => { setMinimized(true); setOpen(false); }} data-testid="multi-entry-minimize-btn">
                <Minus className="mr-1 h-4 w-4" /> Minimize
              </Button>
            </div>
          </DialogHeader>
          <div className="space-y-3">
            {entries.map((e, i) => (
              <div key={i} className="rounded-md border p-2" data-testid="multi-entry-row">
                <div className="flex flex-wrap items-end gap-1.5">
                  <span className="pb-2 text-xs font-bold text-slate-500">{i + 1}.</span>
                  {[["branch", "Branch"], ["transaction_date", "Trans. Date"], ["cheque_no", "Cheque"], ["mode", "DD/RTGS/NEFT"], ["mr_no", "MR No."], ["mr_date", "MR Date"], ["mr_amount", "MR Amt"], ["credit_amount", "Received"], ["loading_charge", "Loading"], ["unloading_charge", "Unloading"], ["vehicle_hire", "Veh. Hire"], ["vehicle_advance", "Veh. Adv."], ["vehicle_balance", "Veh. Bal."], ["salary_expenses", "Salary"], ["office_rent", "Rent"], ["other_expenses", "Other"], ["balance", "Balance"], ["narration", "Narration"]].map(([k, l]) => (
                    <div key={k}>
                      <div className="text-[10px] font-semibold uppercase text-slate-500">{l}</div>
                      {k === "branch" ? (
                        <Select value={e.branch} onValueChange={(v) => upd(i, "branch", v)}>
                          <SelectTrigger className="h-8 w-[100px] text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>{["Chennai", "Tada", "Hyderabad", "Jaipur"].map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : k === "mode" ? (
                        <Select value={e.mode || "none"} onValueChange={(v) => upd(i, "mode", v === "none" ? "" : v)}>
                          <SelectTrigger className="h-8 w-[90px] text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>{["none", "DD", "RTGS", "NEFT", "Cheque", "Cash", "UPI"].map((m) => <SelectItem key={m} value={m}>{m === "none" ? "—" : m}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : (
                        <Input
                          type={k === "transaction_date" || k === "mr_date" ? "date" : NUM_KEYS.includes(k) ? "number" : "text"}
                          className={`h-8 text-xs ${k === "narration" ? "w-[160px]" : k === "transaction_date" || k === "mr_date" ? "w-[130px]" : "w-[84px]"}`}
                          value={e[k]}
                          onChange={(ev) => upd(i, k, ev.target.value)}
                          data-testid={`me-${k}-${i}`}
                        />
                      )}
                    </div>
                  ))}
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => setEntries((es) => es.filter((_, j) => j !== i))} disabled={entries.length === 1} data-testid={`me-delete-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            <div className="flex items-center justify-between">
              <Button variant="outline" onClick={() => setEntries((es) => [...es, { ...BLANK_ROW(), branch: es[es.length - 1].branch, transaction_date: es[es.length - 1].transaction_date }])} data-testid="me-add-row-btn">
                <Plus className="mr-2 h-4 w-4" /> Add Row
              </Button>
              <Button onClick={saveAll} disabled={saving} data-testid="me-save-all-btn">{saving ? "Saving…" : `Save All (${entries.length})`}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const BRANCHES = ["Chennai", "Tada", "Hyderabad", "Jaipur"];
const AMT_FIELDS = [
  ["loading_charge", "Loading Charge"],
  ["unloading_charge", "Unloading Charge"],
  ["vehicle_hire", "Vehicle Hire"],
  ["vehicle_advance", "Vehicle Advance"],
  ["vehicle_balance", "Vehicle Balance"],
  ["salary_expenses", "Salary Expences"],
  ["office_rent", "Office Rent"],
  ["other_expenses", "Other Expences"],
];

const exportBankCsv = (list) => {
  const headers = ["Sl No.", "Branch", "Transaction Date", "Cheque No.", "DD/RTGS/NEFT", "MR No.", "MR Date", "MR Amount", "Deposit Date", "Withdrawal Date", "Amount Received", ...AMT_FIELDS.map(([, l]) => l), "Balance", "Narration", "Remarks"];
  const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const lines = [headers.join(",")];
  list.forEach((r, i) =>
    lines.push([i + 1, r.branch || "", r.transaction_date || "", r.cheque_no || "", r.mode || "", r.mr_no || "", r.mr_date || "", r.mr_amount || 0, r.deposit_date || "", r.withdrawal_date || "", r.credit_amount || 0, ...AMT_FIELDS.map(([k]) => r[k] || 0), r.balance || 0, r.narration || "", r.remarks || ""].map(cell).join(","))
  );
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `ntc-bank-statement-${today()}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export default function BankStatement() {
  return (
    <EntityManager
      title="Bank Statement"
      subtitle="Daily bank statement entries — deposits, withdrawals, charges and running balance."
      endpoint="bank-statements"
      entityName="Entry"
      selectable
      filters={[
        {
          key: "q",
          label: "Search",
          type: "text",
          placeholder: "MR no / cheque / branch / narration",
          match: (r, v) => {
            const q = String(v || "").trim().toLowerCase();
            if (!q) return true;
            return [r.mr_no, r.cheque_no, r.mode, r.branch, r.narration, r.remarks, r.mr_amount, r.credit_amount, r.transaction_date]
              .some((x) => String(x || "").toLowerCase().includes(q));
          },
        },
      ]}
      exportCsv={exportBankCsv}
      headerExtra={(rows) => <BankHeaderTools rows={rows} />}
      onPrintSelected={(list) => printBankStatement(list)}
      defaults={{ branch: "Chennai", transaction_date: today(), cheque_no: "", mode: "", mr_no: "", mr_date: "", mr_amount: 0, deposit_date: "", withdrawal_date: "", credit_amount: 0, loading_charge: 0, unloading_charge: 0, vehicle_hire: 0, vehicle_advance: 0, vehicle_balance: 0, salary_expenses: 0, office_rent: 0, other_expenses: 0, balance: 0, narration: "", remarks: "" }}
      fields={[
        { key: "branch", label: "Branch", type: "select", options: BRANCHES },
        { key: "transaction_date", label: "Transaction Date", type: "date" },
        { key: "cheque_no", label: "Cheque No.", type: "text", placeholder: "Cheque / Ref no." },
        { key: "mode", label: "DD/RTGS/NEFT", type: "select", options: ["", "DD", "RTGS", "NEFT", "Cheque", "Cash", "UPI"] },
        { key: "mr_no", label: "MR No.", type: "text", placeholder: "MR number" },
        { key: "mr_date", label: "MR Date", type: "date" },
        { key: "mr_amount", label: "MR Amount (₹)", type: "number", placeholder: "0" },
        { key: "deposit_date", label: "Deposit Date", type: "date" },
        { key: "withdrawal_date", label: "Withdrawal Date", type: "date" },
        { key: "credit_amount", label: "Amount Received (₹)", type: "number", placeholder: "0" },
        ...AMT_FIELDS.map(([k, l]) => ({ key: k, label: `${l} (₹)`, type: "number", placeholder: "0" })),
        { key: "balance", label: "Balance (₹)", type: "number", placeholder: "0" },
        { key: "narration", label: "Narration", type: "textarea", placeholder: "Transaction details" },
        { key: "remarks", label: "Remarks", type: "textarea", placeholder: "Notes" },
      ]}
      columns={[
        { key: "branch", label: "Branch", render: (r) => r.branch || "—" },        { key: "transaction_date", label: "Trans. Date", render: (r) => (r.transaction_date ? fmtDate(r.transaction_date) : "—") },
        { key: "cheque_no", label: "Cheque No.", render: (r) => r.cheque_no || "—" },
        { key: "mode", label: "DD/RTGS/NEFT", render: (r) => r.mode || "—" },
        { key: "mr_no", label: "MR No.", render: (r) => r.mr_no || "—" },
        { key: "mr_date", label: "MR Date", render: (r) => (r.mr_date ? fmtDate(r.mr_date) : "—") },
        { key: "mr_amount", label: "MR Amount", align: "right", render: (r) => (Number(r.mr_amount) ? inr(r.mr_amount) : "—") },
        { key: "deposit_date", label: "Deposit", render: (r) => (r.deposit_date ? fmtDate(r.deposit_date) : "—") },
        { key: "withdrawal_date", label: "Withdrawal", render: (r) => (r.withdrawal_date ? fmtDate(r.withdrawal_date) : "—") },
        { key: "credit_amount", label: "Received", align: "right", render: (r) => (Number(r.credit_amount) ? <span className="font-medium text-emerald-600">{inr(r.credit_amount)}</span> : "—") },
        ...AMT_FIELDS.map(([k, l]) => ({ key: k, label: l, align: "right", render: (r) => (Number(r[k]) ? inr(r[k]) : "—") })),
        { key: "balance", label: "Balance", align: "right", render: (r) => inr(r.balance) },
        { key: "narration", label: "Narration", render: (r) => (r.narration ? <span className="block max-w-[160px] truncate" title={r.narration}>{r.narration}</span> : "—") },
        { key: "remarks", label: "Remarks", render: (r) => (r.remarks ? <span className="block max-w-[120px] truncate" title={r.remarks}>{r.remarks}</span> : "—") },
      ]}
      footerRow={(rows) => {
        const sum = (k) => rows.reduce((s, r) => s + (Number(r[k]) || 0), 0);
        return (
          <TableRow className="bg-slate-50 font-bold hover:bg-slate-50" data-testid="bank-totals-row">
            <TableCell colSpan={7}>TOTAL ({rows.length} entries)</TableCell>
            <TableCell className="text-right tabular-nums">{inr(sum("mr_amount"))}</TableCell>
            <TableCell colSpan={2} />
            <TableCell className="text-right tabular-nums text-emerald-600">{inr(sum("credit_amount"))}</TableCell>
            {AMT_FIELDS.map(([k]) => (
              <TableCell key={k} className="text-right tabular-nums">{inr(sum(k))}</TableCell>
            ))}
            <TableCell colSpan={4} />
          </TableRow>
        );
      }}
    />
  );
}
